import { Injectable, inject, signal } from '@angular/core';
import { Photo } from '@entities/photo.class';
import { PhotoBlobService } from '@services/core/photo-blob.service';

/** Largeur des miniatures (px) : nette pour une vignette ou une tuile de galerie. */
const LARGEUR_VIGNETTE = 320;
/** Miniatures gardées en mémoire au plus (≈ 0,3 Mo décodées chacune). */
const MAX_VIGNETTES = 300;

/**
 * Miniatures des photos pour l'affichage en petit (vignettes du rapport, galerie).
 *
 * Côté technicien, les photos sont stockées sur l'appareil en 1024 px. Une vignette de
 * quelques dizaines de pixels affichait pourtant la photo ENTIÈRE : le navigateur la
 * décode en pleine taille (~3 Mo chacune), une fois par vignette présente à l'écran.
 * Sur un gros dossier (90 photos), la galerie et la page du rapport retenaient ainsi
 * des centaines de Mo d'images : les iPad finissaient par recharger la page (« page
 * blanche » intermittente, 02/10).
 *
 * Ici, chaque photo est réduite UNE fois à 320 px (≈ 0,3 Mo décodée), à la demande, une
 * photo à la fois (jamais plusieurs grandes images décodées ensemble), et gardée dans un
 * cache borné. La photo entière reste utilisée pour le zoom, l'annotation et l'envoi.
 * Hors technicien (URL S3 déjà miniaturisée côté serveur), rien ne change.
 */
@Injectable({ providedIn: 'root' })
export class VignetteService {
	private photoBlobService = inject(PhotoBlobService);

	/** photoId → URL de la miniature. L'ordre d'insertion sert d'ordre LRU. */
	private cache = new Map<string, string>();
	private demandes = new Set<string>();
	private echecs = new Set<string>();
	private file: Promise<void> = Promise.resolve();

	/** Incrémenté à chaque miniature prête : les vues qui l'ont lu se rafraîchissent. */
	readonly version = signal(0);

	/**
	 * URL à afficher en petit pour cette photo :
	 *  - URL non locale (S3, déjà miniature) : telle quelle ;
	 *  - miniature prête : la miniature ;
	 *  - miniature impossible : la photo entière (comportement d'avant) ;
	 *  - sinon : '' le temps de la préparer (l'icône reste affichée), sans décoder la
	 *    photo entière entre-temps.
	 */
	urlAffichage(photo: Photo | null | undefined, urlEntiere: string | null | undefined): string {
		this.version(); // dépendance : la vue se rafraîchit quand une miniature arrive
		const url = urlEntiere || '';
		if (!photo?.id || !url.startsWith('blob:')) return url;
		const vignette = this.cache.get(photo.id);
		if (vignette) return vignette;
		if (this.echecs.has(photo.id)) return url;
		this.demander(photo.id);
		return '';
	}

	/**
	 * Prépare à l'avance les miniatures d'un dossier (une à la fois, en arrière-plan), pour
	 * que le texte des phrases et l'éditeur manuel les trouvent prêtes à l'ouverture.
	 */
	prechauffer(photoIds: string[]): void {
		for (const id of photoIds) {
			if (id && !this.cache.has(id) && !this.echecs.has(id)) this.demander(id);
		}
	}

	/** Libère les miniatures des photos qui ne sont pas dans `aGarder` (changement de dossier). */
	libererSauf(aGarder: Set<string>): void {
		for (const [id, url] of this.cache) {
			if (!aGarder.has(id)) {
				URL.revokeObjectURL(url);
				this.cache.delete(id);
			}
		}
		this.echecs.clear();
	}

	/** Cette URL est-elle une de nos miniatures ? */
	estVignette(url: string | null | undefined): boolean {
		if (!url) return false;
		for (const v of this.cache.values()) if (v === url) return true;
		return false;
	}

	/**
	 * Une miniature n'a pas pu s'afficher : on l'abandonne pour cette photo, qui
	 * s'affichera en taille réelle (comportement d'avant). La photo stockée n'est pas en
	 * cause et ne doit surtout pas être purgée.
	 */
	abandonner(photoId: string): void {
		const url = this.cache.get(photoId);
		if (url) URL.revokeObjectURL(url);
		this.cache.delete(photoId);
		this.echecs.add(photoId);
		this.version.update(v => v + 1);
	}

	/** Nombre de miniatures en mémoire (télémétrie). */
	get taille(): number {
		return this.cache.size;
	}

	private demander(photoId: string): void {
		if (this.demandes.has(photoId)) return;
		this.demandes.add(photoId);
		// File : une seule photo décodée à la fois.
		this.file = this.file.then(async () => {
			try {
				const blob = await this.photoBlobService.getBlob(photoId);
				const vignette = blob ? await reduire(blob) : null;
				if (!vignette) { this.echecs.add(photoId); return; }
				this.cache.set(photoId, URL.createObjectURL(vignette));
				while (this.cache.size > MAX_VIGNETTES) {
					const [plusAncien, url] = this.cache.entries().next().value as [string, string];
					URL.revokeObjectURL(url);
					this.cache.delete(plusAncien);
				}
			} catch {
				this.echecs.add(photoId);
			} finally {
				this.demandes.delete(photoId);
				this.version.update(v => v + 1);
			}
		});
	}
}

/** Réduit une image à LARGEUR_VIGNETTE de large (JPEG). null si le navigateur n'y arrive pas. */
async function reduire(blob: Blob): Promise<Blob | null> {
	const parBitmap = await reduireParBitmap(blob);
	return parBitmap !== undefined ? parBitmap : reduireParImage(blob);
}

/**
 * Réduction sans passer par une <img> : décodée directement à la taille de la miniature.
 * Par une image, chaque photo décodée en pleine taille restait dans le cache d'images du
 * navigateur : ~450 Mo pour 78 photos à l'ouverture d'un rapport, jamais rendus sous
 * WebKit même après retour au tableau de bord (mesuré en local, 08/10).
 * undefined : réduction non prise en charge par ce navigateur, passer à la méthode image.
 */
async function reduireParBitmap(blob: Blob): Promise<Blob | null | undefined> {
	if (typeof createImageBitmap !== 'function') return undefined;
	let bitmap: ImageBitmap;
	try {
		bitmap = await createImageBitmap(blob, { resizeWidth: LARGEUR_VIGNETTE, resizeQuality: 'medium' });
	} catch {
		return undefined;
	}
	try {
		if (bitmap.width > LARGEUR_VIGNETTE) return undefined; // option de taille ignorée
		const canvas = document.createElement('canvas');
		canvas.width = bitmap.width;
		canvas.height = bitmap.height;
		const ctx = canvas.getContext('2d');
		if (!ctx) return undefined;
		ctx.drawImage(bitmap, 0, 0);
		const resultat = await new Promise<Blob | null>(resolve => canvas.toBlob(resolve, 'image/jpeg', 0.8));
		canvas.width = 0;
		canvas.height = 0;
		return resultat ?? undefined;
	} finally {
		bitmap.close();
	}
}

async function reduireParImage(blob: Blob): Promise<Blob | null> {
	const url = URL.createObjectURL(blob);
	const img = new Image();
	try {
		img.src = url;
		await img.decode();
		const largeur = Math.min(LARGEUR_VIGNETTE, img.naturalWidth || LARGEUR_VIGNETTE);
		const hauteur = Math.max(1, Math.round((img.naturalHeight / (img.naturalWidth || 1)) * largeur));
		const canvas = document.createElement('canvas');
		canvas.width = largeur;
		canvas.height = hauteur;
		const ctx = canvas.getContext('2d');
		if (!ctx) return null;
		ctx.drawImage(img, 0, 0, largeur, hauteur);
		const resultat = await new Promise<Blob | null>(resolve => canvas.toBlob(resolve, 'image/jpeg', 0.8));
		// Libère au plus vite la mémoire du canevas (Safari la garde sinon).
		canvas.width = 0;
		canvas.height = 0;
		return resultat;
	} catch {
		return null;
	} finally {
		img.src = '';
		URL.revokeObjectURL(url);
	}
}
